import { AdyenCheckout, ApplePay, Card, GooglePay } from "@adyen/adyen-web";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { ADYEN_ENV } from "../../common/envconst";
import { useIsMounted } from "../../common/hooks/useIsMounted";
import { Nullable } from "../../common/types/types";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import {
	IApplePayMethod,
	IGooglePayMethod,
	IPaymentMethod,
} from "../../contexts/PaymentContext/types";
import { useAdyenValidation } from "./useAdyenValidation";

// Helper functions to extract payment method configurations
export const getApplePayConfig = (
	paymentMethods: IPaymentMethod[]
): IApplePayMethod["configuration"] | null => {
	if (!Array.isArray(paymentMethods)) {
		return null;
	}

	const applePayMethod = paymentMethods.find(
		(method): method is IApplePayMethod => method.type === "applepay"
	);

	return applePayMethod?.configuration || null;
};

export const getGooglePayConfig = (
	paymentMethods: IPaymentMethod[]
): IGooglePayMethod["configuration"] | null => {
	if (!Array.isArray(paymentMethods)) {
		return null;
	}

	const googlePayMethod = paymentMethods.find(
		(method): method is IGooglePayMethod => method.type === "googlepay"
	);

	return googlePayMethod?.configuration || null;
};

interface AdyenCardHookProps {
	cardContainerRef: RefObject<HTMLElement>;
	applePayContainerRef: RefObject<HTMLElement>;
	googlePayContainerRef: RefObject<HTMLElement>;
	onApplePayPayment: (data: any, component?: any, actions?: any) => void;
	onGooglePayPayment: (data: any, component?: any, actions?: any) => void;
	onPaymentSubmit: (data: any, component?: any, actions?: any) => void;
	onPaymentCompleted: (result: any, component?: any) => void;
	onAdditionalDetails?: (state: any, component?: any) => void;
}
export const useAdyenCard = ({
	cardContainerRef,
	applePayContainerRef,
	googlePayContainerRef,
	onPaymentSubmit,
	onApplePayPayment,
	onGooglePayPayment,
	onPaymentCompleted,
	onAdditionalDetails,
}: AdyenCardHookProps) => {
	const { state: amount } = usePaymentContext(
		(state) => state?.paymentData?.orders_figures?.net_value
	);
	const { state: adyenConfig } = useConfigContext((state) => state?.config.adyen_merchant_name);
	const { state: paymentMethods } = usePaymentContext((state) => state?.paymentMethods || []);
	const [cardComponent, setCardComponent] = useState<Nullable<Card>>(null);
	const isMounted = useIsMounted();
	const { isCardValid, updateFieldValidity } = useAdyenValidation();
	const callbacksRef = useRef({
		onPaymentSubmit,
		onPaymentCompleted,
		onAdditionalDetails,
		onApplePayPayment,
		onGooglePayPayment,
	});

	useEffect(() => {
		callbacksRef.current = {
			onPaymentSubmit,
			onPaymentCompleted,
			onAdditionalDetails,
			onApplePayPayment,
			onGooglePayPayment,
		};
	});

	// Initialize Adyen components once when containers are available
	useEffect(() => {
		if (
			!cardContainerRef.current ||
			!applePayContainerRef.current ||
			!googlePayContainerRef.current
		) {
			return;
		}

		let cardInstance: Card | null = null;
		let applePayInstance: ApplePay | null = null;
		let googlePayInstance: GooglePay | null = null;
		let isCancelled = false;

		const initializeAdyen = async () => {
			const applePayConfig = getApplePayConfig(paymentMethods);
			const googlePayConfig = getGooglePayConfig(paymentMethods);

			console.info(
				"Adyen",
				"\nADYEN_ENV:",
				ADYEN_ENV,
				"\nADYEN_NAME:",
				adyenConfig?.name,
				"\nADYEN_CLIENT_KEY:",
				adyenConfig?.client_key?.substring(0, 8),
				"\nAPPLE_PAY_MERCHANT_ID:",
				applePayConfig?.merchantId?.substring(0, 8) || "Not Available",
				"\nAPPLE_PAY_MERCHANT_NAME:",
				applePayConfig?.merchantName?.substring(0, 8) || "Not Available",
				"\nGOOGLE_PAY_MERCHANT_ID:",
				googlePayConfig?.merchantId?.substring(0, 8) || "Not Available",
				"\nGOOGLE_PAY_GATEWAY_MERCHANT_ID:",
				googlePayConfig?.gatewayMerchantId?.substring(0, 8) || "Not Available"
			);
			try {
				const checkout = await AdyenCheckout({
					environment: ADYEN_ENV || "test",
					countryCode: "SG",
					clientKey: adyenConfig?.client_key,
					onError: (error) => console.error("Adyen Init Error", error),
				});

				if (isCancelled) return;

				cardInstance = new Card(checkout, {
					showPayButton: false,
					onFieldValid: updateFieldValidity,
					onSubmit: (state, component, actions) => {
						if (state.isValid) {
							callbacksRef.current.onPaymentSubmit(state.data, component, actions);
						}
					},
					onConfigSuccess: () => {},
					onPaymentCompleted: (result, component) => {
						callbacksRef.current.onPaymentCompleted(result, component);
					},
					onAdditionalDetails: (state, component) => {
						if (callbacksRef.current.onAdditionalDetails) {
							callbacksRef.current.onAdditionalDetails(state, component);
						}
					},
				});

				// Only initialize Apple Pay in production or HTTPS environment
				const applePayConfig = getApplePayConfig(paymentMethods);

				if (
					(globalThis.location?.protocol === "https:" || ADYEN_ENV === "live") &&
					applePayConfig
				) {
					applePayInstance = new ApplePay(checkout, {
						buttonColor: "black",
						amount: {
							value: amount ?? 0,
							currency: "SGD",
						},
						configuration: applePayConfig,
						isExpress: true,
						onAuthorized: (data, actions) => {
							callbacksRef.current.onApplePayPayment(data, actions);
						},

						onSubmit: (state, component, actions) => {
							const { riskData, paymentMethod, clientStateDataIndicator, ...others } = state.data;

							actions.resolve({
								...others,
								resultCode: "Authorised",
							});
						},
						onPaymentCompleted: (result, component) => {
							callbacksRef.current.onPaymentCompleted(result, component);
						},
						onOrderTrackingRequest: (resolve, reject) => {
							try {
								resolve({
									orderTypeIdentifier: "",
									orderIdentifier: "",
									webServiceURL: "",
									authenticationToken: "",
								});
							} catch (error) {
								console.error("Failed to onOrderTrackingRequest applepay components:", error);
								reject();
							}

							return null;
						},
						onApplePayCodeClose: () => {
							console.info("onApplePayCodeClose");
						},
					});
				}

				const googlePayConfig = getGooglePayConfig(paymentMethods);

				if (googlePayConfig) {
					googlePayInstance = new GooglePay(checkout, {
						buttonType: "long",
						buttonColor: "default",
						amount: {
							value: amount ?? 0,
							currency: "SGD",
						},
						configuration: googlePayConfig,
						emailRequired: false,
						shippingAddressRequired: false,
						allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
						allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX"],
						isExpress: true,
						onClick: (resolve, reject) => {
							try {
								resolve();
							} catch (error) {
								console.error("Google Pay onClick error:", error);
								reject(error);
							}
						},
						onSubmit: (state, component, actions) => {
							const { riskData, paymentMethod, clientStateDataIndicator, ...others } = state.data;

							actions.resolve({
								...others,
								resultCode: "Authorised",
							});
						},
						onAuthorized: (paymentData, actions) => {
							try {
								callbacksRef.current.onGooglePayPayment(paymentData, null, actions);
							} catch (error) {
								console.error("Google Pay authorization error:", error);
								if (actions?.reject) {
									actions.reject({
										transactionState: "ERROR",
										error: {
											reason: "PAYMENT_AUTHORIZATION",
											message: "Payment authorization failed",
											intent: "PAYMENT_AUTHORIZATION",
										},
									});
								}
							}
						},
						onPaymentCompleted: (result, component) => {
							callbacksRef.current.onPaymentCompleted(result, component);
						},
					});
				}

				if (isCancelled) return;

				if (isMounted()) {
					cardInstance.mount(cardContainerRef.current!);
					setCardComponent(cardInstance);

					if (applePayInstance) {
						applePayInstance.mount(applePayContainerRef.current!);
					}

					// Mount Google Pay only if available
					if (googlePayInstance) {
						googlePayInstance
							.isAvailable()
							.then(() => {
								if (isMounted() && googlePayContainerRef.current) {
									googlePayInstance?.mount(googlePayContainerRef.current);
								}
							})
							.catch((error) => {
								console.info("Google Pay not available:", error);
							});
					}
				}
			} catch (error) {
				if (!isCancelled) {
					console.error(
						"Failed to initialize Adyen components:",
						error,
						applePayContainerRef.current
					);
				}
			}

			return null;
		};

		initializeAdyen();

		return () => {
			isCancelled = true;

			if (cardInstance?.unmount) {
				try {
					cardInstance.unmount();
				} catch (error) {
					console.error("Error unmounting card instance:", error);
				}
			}

			if (applePayInstance?.unmount) {
				try {
					applePayInstance.unmount();
				} catch (error) {
					console.error("Error unmounting Apple Pay instance:", error);
				}
			}

			if (googlePayInstance?.unmount) {
				try {
					googlePayInstance.unmount();
				} catch (error) {
					console.error("Error unmounting Google Pay instance:", error);
				}
			}
		};
	}, [
		cardContainerRef,
		applePayContainerRef,
		googlePayContainerRef,
		isMounted,
		updateFieldValidity,
		amount,
		paymentMethods,
	]);

	// Memoize submit function
	const submit = useCallback(() => {
		cardComponent?.submit();
	}, [cardComponent]);

	return {
		isCardValid,
		submit,
	};
};
